<script setup>
import MainHeader from "@/Layouts/MainHeader.vue";
import Body from "@/Layouts/Body.vue";
import MainFooter from "@/Layouts/MainFooter.vue";
import Tracking from '@/Components/ResourseLoader.vue';
import Chart from '@/Components/Chart.vue';
import Answer from "@/Layouts/Answer.vue";
import Answers from "@/Layouts/Answers.vue";
import ShareButtons from "@/Components/ShareButtons.vue";
import PreferredSource from "@/Components/PreferredSource.vue";
import RatingReady from "@/Components/RatingReady.vue";
import VKPost from "@/Components/VKPost.vue";
import ArticleToc from "@/Components/ArticleToc.vue";
import MaxButton from "@/Components/MaxButton.vue";
import ArticleExpertOpinions from "@/Components/ArticleExpertOpinions.vue";
import PaywallLock from "@/Components/PaywallLock.vue";
import RelatedArticles from "@/Layouts/RelatedArticles.vue";
import { Head, Link } from "@inertiajs/vue3";
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useModal } from "vue-final-modal";
import ModalPhoneConfirm from "@/Components/ModalPhoneConfirm.vue";

let vars = defineProps({
  article: Object,
  totalComments: Number,
  user: Object,
  usluga: Object,
  auth: Object,
  region: Object,
  question: Object,
  answers: Object,
  authid: Number,
  stats: Object,
  cityheader: Object,
  backendurl: String,
  statistics: Object,
  toc: { type: Array, default: () => [] },
  expertOpinions: { type: Object, default: () => ({ canAdd: false, canModerate: false, pending: [], min: 30, max: 800 }) },
  relatedArticles: { type: Array, default: () => [] },
  relatedPractice: { type: Array, default: () => [] },
  coverDims: { type: Object, default: null },
  userCityLink: { type: Object, default: () => ({ mode: 'hub' }) },
  paywall: { type: Object, default: () => ({ has: false, open: true }) },
});

// Заголовок страницы. Приставку с услугой даём только короткому заголовку —
// иначе она вытесняет из выдачи полезные слова, а не дополняет их.
const pageTitle = computed(() => {
  const h = (vars.article?.header ?? "").trim();
  const u = (vars.usluga?.usl_name ?? "").trim();
  return u && h.length < 45 ? `${h} - ${u}` : h;
});

// Разбор поля видео: vkvid (video_ext, плеер) → vk (пост-виджет) → legacy-iframe
const videoRef = (() => {
  const raw = (vars.article.youtube_file_path || "").trim();
  if (!raw) return null;
  // VK Клип: компактный vkclip:oid:id или ссылка vk.com/clip-OID_VID → clip_ext (вертикалка)
  if (raw.startsWith("vkclip:")) {
    const p = raw.split(":");
    if (p[1] && p[2]) return { type: "vkvideo", src: `https://vk.com/clip_ext.php?oid=-${p[1]}&id=${p[2]}`, w: 0, h: 0 };
    return null;
  }
  const cm = raw.match(/clip-(\d+)_(\d+)/i);
  if (cm) return { type: "vkvideo", src: `https://vk.com/clip_ext.php?oid=-${cm[1]}&id=${cm[2]}`, w: 0, h: 0 };
  // VK видео/клип (video_ext): компактный формат vkvid:oid:id:hash:w:h
  if (raw.startsWith("vkvid:")) {
    const p = raw.split(":");
    const oid = p[1], id = p[2], hash = p[3] || "";
    if (oid && id) return {
      type: "vkvideo",
      src: `https://vk.com/video_ext.php?oid=${oid}&id=${id}&hash=${hash}`,
      w: parseInt(p[4], 10) || 0, h: parseInt(p[5], 10) || 0,
    };
    return null;
  }
  // сырой video_ext URL/iframe (легаси)
  if (/video_ext\.php/i.test(raw)) {
    const m = raw.match(/video_ext\.php\?[^"'\s<>]+/i);
    if (m) return { type: "vkvideo", src: "https://vk.com/" + m[0].replace(/&amp;/g, "&"), w: 0, h: 0 };
  }
  // VK запись (пост-виджет): vk:owner:post:hash
  if (raw.startsWith("vk:")) {
    const p = raw.split(":");
    const owner = parseInt(p[1], 10), post = parseInt(p[2], 10);
    if (owner && post) return { type: "vk", owner, post, hash: p.slice(3).join(":") || "" };
    return null;
  }
  const m = raw.match(/vk\.com\/wall(-?\d+)_(\d+)/i);
  if (m) return { type: "vk", owner: parseInt(m[1], 10), post: parseInt(m[2], 10), hash: "" };
  if (/^https?:\/\//i.test(raw)) return { type: "iframe", src: raw };
  return null;
})();

// Соотношение сторон и ориентация видео (для вертикальных клипов)
const videoAspect = (videoRef?.type === "vkvideo" && videoRef.w && videoRef.h)
  ? `${videoRef.w} / ${videoRef.h}`
  : (videoRef?.type === "vkvideo" ? "9 / 16" : "16 / 9");
const isVertical = videoRef?.type === "vkvideo"
  ? (videoRef.w && videoRef.h ? videoRef.h > videoRef.w : true)
  : false;

// Разбивка тела для вставки видео в середину: сервер ставит маркер перед центральным h2
const VIDEO_MARKER = "<!--VIDEO_HERE-->";
const bodyParts = (() => {
  const raw = vars.article.body || "";
  if (!videoRef) return { top: raw.split(VIDEO_MARKER).join(""), bottom: "", split: false };
  const i = raw.indexOf(VIDEO_MARKER);
  if (i !== -1) return { top: raw.slice(0, i), bottom: raw.slice(i + VIDEO_MARKER.length), split: true };
  // видео есть, но маркер не вставлен (мало заголовков) — показываем видео перед телом
  return { top: "", bottom: raw, split: true };
})();

// ISO-даты для og:article и теги из SEO-ключей
function toIso(s) {
  if (!s) return "";
  try { return new Date(String(s).replace(" ", "T")).toISOString(); } catch (e) { return ""; }
}
// Сервер отдаёт даты уже с поясом (created_iso/updated_iso); toIso — запасной путь для старых данных
const publishedIso = vars.article.created_iso || toIso(vars.article.created_at);
// Картинка для превью ссылки (Telegram, ВК, мессенджеры): обложка → фото автора → логотип.
// ⚠ Без обложки раньше уходило «https://nedicom.ru/null» — карточка без картинки.
const ogImage = 'https://nedicom.ru/' + (vars.article.practice_file_path || vars.article.avatar_path || 'logoq.webp');
const modifiedIso = vars.article.updated_iso || toIso(vars.article.updated_at) || publishedIso;
const keywordTags = (() => {
  try {
    const v = vars.article.seo_keywords;
    const arr = Array.isArray(v) ? v : (typeof v === "string" && v.trim() ? JSON.parse(v) : []);
    return (arr || []).filter((x) => typeof x === "string" && x.trim()).slice(0, 8);
  } catch (e) { return []; }
})();

let avito = vars.article.avito ? vars.article.avito.includes('avito') : null;

// Судебная практика (PDF) — приходит JSON-строкой из DB::table (без каста модели)
const practicePdfs = (() => {
  try {
    const v = vars.article.practice_pdfs;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.trim()) return JSON.parse(v);
  } catch (e) {}
  return [];
})();

function maskPhone(phone) {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 2) return phone;
  const visible = digits.slice(0, -2);
  let out = '', idx = 0;
  for (const ch of phone) {
    if (/\d/.test(ch)) { out += idx < visible.length ? visible[idx] : '*'; idx++; }
    else out += ch;
  }
  return out;
}

const articlePhoneTo = vars.article.phone ? 'tel:' + vars.article.phone : null;

// Склонение «год / года / лет» для стажа
function yearWord(n) {
  const a = Math.abs(Number(n)) % 100, b = a % 10;
  if (a > 10 && a < 20) return 'лет';
  if (b > 1 && b < 5) return 'года';
  if (b === 1) return 'год';
  return 'лет';
}

// Склонение «отзыв / отзыва / отзывов»
function reviewWord(n) {
  const a = Math.abs(Number(n)) % 100, b = a % 10;
  if (a > 10 && a < 20) return 'отзывов';
  if (b > 1 && b < 5) return 'отзыва';
  if (b === 1) return 'отзыв';
  return 'отзывов';
}

// Регион работы юриста — из локации статьи: «Республика Крым» → «Крым», иначе как есть
const lawyerRegion = (() => {
  const r = (vars.region?.region || '').trim();
  if (!r || r === 'Россия') return '';
  if (/крым/i.test(r)) return 'Крым';
  return r; // «Севастополь» и пр.
})();

const { open: openArticlePhone, close: closeArticlePhone } = useModal({
  component: ModalPhoneConfirm,
  attrs: {
    phone: vars.article.phone,
    phoneto: articlePhoneTo,
    title: vars.article.name ?? "Мина и партнёры",
    subtitle: vars.usluga?.usl_name ?? "Юридическая помощь",

    onClose() { closeArticlePhone(); },
  },
});

// Нижнее плавающее меню (стрелка вверх / вопрос / комментарии) — показываем
// только после того, как пользователь немного прокрутил страницу вниз.
const showBottomNav = ref(false);
// Полоса прогресса чтения: доля пройденного тела статьи (а не всей страницы с комментариями)
const readProgress = ref(0);
const bodyEl = ref(null);
const mounted = ref(false); // правая колонка с оглавлением — только в браузере, без дублей ссылок в SSR
const onScroll = () => {
  showBottomNav.value = window.scrollY > 400;
  const el = bodyEl.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const total = r.height - window.innerHeight * 0.6;
  readProgress.value = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
};
// Закрытые фрагменты: сервер оставил пустые блоки .paywall--locked — ставим в них форму для кода
const lockedTargets = ref([]);
onMounted(() => {
  mounted.value = true;
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  lockedTargets.value = [...document.querySelectorAll('.articlebody .paywall--locked')].map((el) => {
    let headings = [];
    try { headings = JSON.parse(el.getAttribute('data-headings') || '[]'); } catch (e) {}
    return { el, words: Number(el.getAttribute('data-words')) || 0, headings };
  });

  // Подсказки к терминам: фокус с клавиатуры и нажатие на телефоне показывают текст подсказки
  const alignTip = (el) => {
    // У правого края колонки подсказка раскрывается влево — иначе её срезает overflow колонки
    const col = el.closest('.articlebody');
    if (!col) return;
    const room = col.getBoundingClientRect().right - el.getBoundingClientRect().left;
    el.toggleAttribute('data-tip-left', room < 300);
  };
  document.querySelectorAll('.articlebody span[data-tip]').forEach((el) => {
    el.addEventListener('mouseenter', () => alignTip(el));
    el.addEventListener('focus', () => alignTip(el));
    el.setAttribute('tabindex', '0');
    el.setAttribute('role', 'note');
    el.setAttribute('aria-label', el.textContent + ': ' + el.getAttribute('data-tip'));
  });
});

// «15 сентября 2026» вместо «15-Сентября-2026»
const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
function humanDate(s, fallback) {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${+m[3]} ${MONTHS[+m[2] - 1]} ${m[1]}` : fallback;
}
const hasToc = (vars.toc || []).length >= 3;

// Время чтения по словам текста (≈180 слов в минуту), а не по длине HTML с тегами
const readMinutes = Math.max(1, Math.round(((vars.article.body || '').replace(/<[^>]+>/g, ' ').match(/\S+/g) || []).length / 180));

// Обложка на ПК — внутри текста перед вторым подзаголовком, справа с обтеканием.
// В начале статьи слева уже стоит блок автора: две плавающие колонки сразу зажали бы текст в полоску.
// loading=lazy: на мобиле эта копия скрыта (display:none) и не скачивается.
function withCover(html) {
  const path = vars.article.practice_file_path;
  if (!path || !html) return html;
  const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const dims = vars.coverDims ? ` width="${vars.coverDims.width}" height="${vars.coverDims.height}"` : '';
  const caption = vars.usluga?.usl_name ? `<figcaption>${esc(vars.usluga.usl_name)} — практика</figcaption>` : '';
  const fig = `<figure class="article-cover"><img src="https://nedicom.ru/${esc(path)}" alt="${esc(vars.article.header)} - практика"${dims} loading="lazy" decoding="async">${caption}</figure>`;
  let at = -1, from = 0;
  for (let n = 0; n < 2; n++) { const i = html.indexOf('<h2', from); if (i === -1) break; at = i; from = i + 3; }
  return at === -1 ? fig + html : html.slice(0, at) + fig + html.slice(at);
}
function scrollToTop() {
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
}
function openToc() {
  const el = document.getElementById('toc');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
onUnmounted(() => window.removeEventListener('scroll', onScroll));
</script>


<template>

  <Head>
    <!-- Статья с платным фрагментом: робот видит текст целиком, поэтому запрещаем «сохранённую копию»
         (у Яндекса) — иначе закрытую часть можно было бы прочитать из выдачи -->
    <meta v-if="paywall.has" name="robots" content="noarchive" />
    <!-- Приставка с названием услуги добавляется, ТОЛЬКО если заголовок короткий.
         Google обрезает заголовок по ширине (примерно 55-60 знаков кириллицей), и
         при длинном заголовке приставка не помогает, а вытесняет полезное. Порог 45
         взят с запасом: типичное название услуги — 15-25 знаков плюс разделитель. -->
    <title>{{ pageTitle }}</title>
    <meta name="description" :content="vars.article.description" />
    <meta property="og:title" :content="vars.article.header" />
    <meta property="og:description" :content="vars.article.description" />
    <meta property="og:type" content="article" />
    <meta property="og:url" :content="'https://nedicom.ru/articles/' + vars.article.url" />
    <meta property="og:image" :content="ogImage" />
    <meta property="og:site_name" content="nedicom.ru" />
    <meta property="og:locale" content="ru_RU" />
    <meta v-if="publishedIso" property="article:published_time" :content="publishedIso" />
    <meta v-if="modifiedIso" property="article:modified_time" :content="modifiedIso" />
    <meta v-if="usluga?.usl_name" property="article:section" :content="usluga.usl_name" />
    <meta v-if="vars.article.name" property="article:author" :content="vars.article.name" />
    <meta v-for="t in keywordTags" :key="'atag-' + t" property="article:tag" :content="t" />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" :content="vars.article.header" />
    <meta name="twitter:description" :content="vars.article.description" />
    <meta v-if="vars.article.practice_file_path" name="twitter:image"
      :content="'https://nedicom.ru/' + vars.article.practice_file_path" />
    <meta v-if="coverDims" property="og:image:width" :content="String(coverDims.width)" />
    <meta v-if="coverDims" property="og:image:height" :content="String(coverDims.height)" />
    <link rel="canonical" :href="'https://nedicom.ru/articles/' + vars.article.url" />
  </Head>

  <MainHeader :key="backendurl" :auth="vars.auth" :city="vars.cityheader" :tracking="$page.props.tracking"
    :backendurl="vars.backendurl" />

  <Body>
    <!-- Прогресс чтения: сколько статьи уже пройдено -->
    <div v-if="mounted" aria-hidden="true" class="fixed inset-x-0 top-0 z-[70] h-[3px] pointer-events-none">
      <div class="h-full origin-left bg-indigo-500 transition-transform duration-150 ease-out"
        :style="{ transform: `scaleX(${readProgress})` }"></div>
    </div>

    <!-- ⚠ МИКРОРАЗМЕТКИ (itemscope/itemprop) ЗДЕСЬ НЕТ — только JSON-LD из ArticleController.
         Раньше страница несла второй Article в микроразметке: Google видел два объекта, и во втором
         не было isAccessibleForFree=false — противоречие разметке закрытого фрагмента. -->
    <div class="flex flex-col md:flex-row gap-4 md:gap-6 text-gray-900 px-4">

      <!-- Мобильный низ статьи (вариант A): автор, шаринг, даты. Микроразметка — в блоке для ПК. -->
      <div class="w-full order-2 md:hidden">
        <!-- Карточка автора-юриста (компактная) -->
        <div class="my-3 md:my-4 rounded-xl border border-gray-100 bg-gray-50 p-3">
          <div class="flex items-center gap-3">
            <Link :href="route('lawyer', article.userid)" class="relative shrink-0">
              <img :src="'https://nedicom.ru/' + article.avatar_path" :alt="article.name"
                class="!w-12 !h-12 rounded-full object-cover" />
              <span
                class="-left-0.5 -top-0.5 animate-pulse absolute w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
            </Link>
            <div class="min-w-0">
              <a :href="route('lawyer', article.userid)" :aria-label="'автор статьи - ' + article.name"
                class="hover:underline">
                <span class="block text-sm font-semibold text-gray-900 leading-tight">{{ article.name }}</span>
              </a>
              <span v-if="usluga?.usl_name" class="block text-xs text-indigo-700 leading-tight mt-0.5">{{ usluga.usl_name }}</span>
            </div>
          </div>
          <a v-if="article.lawyer_rating" :href="route('lawyer', article.userid)"
            class="mt-2 flex items-center gap-1.5 hover:opacity-80">
            <RatingReady :rating="article.lawyer_rating" />
            <span class="text-xs text-gray-600 whitespace-nowrap">
              <span class="font-semibold text-gray-800">{{ article.lawyer_rating }}</span> · {{ article.lawyer_reviews_count }} {{ reviewWord(article.lawyer_reviews_count) }}
            </span>
          </a>
          <div v-if="article.expirience || lawyerRegion" class="mt-2 flex flex-wrap gap-1.5 text-xs">
            <span v-if="article.expirience"
              class="inline-flex items-center px-2 py-0.5 rounded-lg bg-white text-gray-600 border border-gray-100">
              Стаж {{ article.expirience }} {{ yearWord(article.expirience) }}
            </span>
            <span v-if="lawyerRegion"
              class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg bg-white text-gray-600 border border-gray-100">
              📍 {{ lawyerRegion }}
            </span>
          </div>
        </div>

        <div class="flex items-center justify-center gap-3 md:gap-5 my-3 md:my-4">
          <!-- Кнопки шаринга -->
          <div class="flex items-center justify-center">
            <ShareButtons :bundle="vars.article" :auth="vars.auth" />
          </div>

          <!-- Счетчик просмотров -->
          <div class="flex items-center justify-center text-xs bg-gray-50 px-3 py-2 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"
              class="w-5 h-5 mr-2 text-gray-600">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
            <p class="font-medium text-gray-700">{{ article.counter }}</p>
          </div>
        </div>

        <div class="mx-auto md:mx-0 mt-4 max-w-xs md:max-w-none rounded-xl bg-gray-50 p-3 space-y-2 text-sm">
          <div class="hidden md:flex items-center gap-2 text-gray-500">
            <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Время чтения</span>
            <span class="ml-auto font-semibold text-gray-700">{{ (vars.article.body.length / 1000).toFixed(0) }} мин</span>
          </div>
          <div class="flex items-center gap-2 text-gray-500">
            <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Создано</span>
            <!-- ⚠ Дата в микроразметке — через <time datetime> с часовым поясом: у div атрибута content нет,
                 а голое «2026-09-15T16:37:28» валидатор помечает «не указан часовой пояс» -->
            <time :datetime="publishedIso" class="ml-auto font-semibold text-gray-700">{{ humanDate(vars.article.created_at, vars.article.created) }}</time>
          </div>
          <div class="flex items-center gap-2 text-gray-500">
            <svg class="w-4 h-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Обновлено</span>
            <time :datetime="modifiedIso" class="ml-auto font-semibold text-gray-700">{{ humanDate(article.updated_at, article.updated) }}</time>
          </div>
        </div>
        <!-- tooltip component -->
      </div>

      <div class="w-full my-3 md:py-12 md:px-5 flex bg-white [overflow:clip] order-1">
        <!-- ⚠ НЕ overflow-hidden: он делает блок скролл-контейнером, и липкое оглавление справа
             перестаёт липнуть к экрану. Обтекание обложки держит flow-root, вылезание по ширине — clip. -->
        <div class="bg-white flow-root min-w-0 w-full">
          <div class="lg:flex lg:justify-center lg:gap-10 xl:gap-16">
          <div class="min-w-0 flex-1 max-w-[52rem] mx-auto lg:mx-0">
          <!-- Хлебные крошки (микроразметку НЕ дублируем: BreadcrumbList отдаётся в JSON-LD графе из ArticleController) -->
          <nav class="mb-4 flex items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
            <span>
              <a href="https://nedicom.ru/" class="hover:underline">Главная</a>
            </span>
            <span class="text-gray-300">·</span>
            <span>
              <a :href="route('lenta.popular')" class="hover:underline">Лента</a>
            </span>
            <span class="text-gray-300">·</span>
            <span class="truncate text-gray-600 min-w-0">{{ usluga?.usl_name || vars.article.header }}</span>
          </nav>

          <!-- Статистика: сервер отдаёт её только при флажке «Показывать статистику» и только автору/админу -->
          <Chart v-if="vars.statistics" :statistics="vars.statistics" />

          <div v-if="vars.auth" class="my-3">
            <div v-if="vars.auth.id == article.userid || vars.auth.isadmin == 1 || vars.auth.is_editor == 1">
              <a :href="route('articles.edit', [article.url])"
                class="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline">Редактировать</a>
            </div>
          </div>



          <h1 v-if="vars.article.header"
            class="my-4 text-3xl font-extrabold leading-tight lg:mb-6 lg:text-4xl dark:text-white lead">
            {{ vars.article.header }}
          </h1>

          <p v-if="article.description" class="my-9 text-2xl lead text-gray-800">
            {{ article.description }}
          </p>

          <!-- Оглавление (jump-to). Якоря проставлены на сервере в h2/h3 тела статьи. -->
          <ArticleToc :items="vars.toc" :class="mounted && hasToc ? 'lg:hidden' : ''" />

          <!-- Компактный CTA с юристом — только мобайл, у верха статьи -->
          <div v-if="article.phone" class="md:hidden my-5 rounded-xl border border-gray-100 bg-gray-50 p-3">
            <div class="flex items-center gap-3">
              <Link :href="route('lawyer', article.userid)" class="relative shrink-0">
                <img :src="'https://nedicom.ru/' + article.avatar_path" :alt="article.name"
                  class="!w-11 !h-11 rounded-full object-cover" />
                <span class="-left-1 -top-1 absolute w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
              </Link>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-gray-800 truncate leading-tight">{{ article.name }}</p>
                <p class="text-xs text-indigo-700 truncate leading-tight">{{ usluga?.usl_name || 'Юрист по теме статьи' }}</p>
              </div>
              <button type="button" onclick="window.goal('OPEN_PHONE');" @click="openArticlePhone"
                class="shrink-0 inline-flex items-center gap-2 py-2 px-3 text-white
                       bg-brand-600 hover:bg-brand-700 rounded-lg shadow
                       hover:scale-105 transition-all duration-300 cursor-pointer focus:outline-none">
                <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span class="text-sm font-bold whitespace-nowrap leading-tight">{{ maskPhone(article.phone) }}</span>
              </button>
            </div>
            <!-- Юридическая проверка (мобайл) -->
            <a v-if="article.reviewer" :href="route('lawyer', article.reviewer.id)"
              class="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5 text-xs text-emerald-900">
              <svg class="w-4 h-4 shrink-0 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span class="min-w-0 truncate">
                {{ article.reviewer.female ? "Проверила" : "Проверено" }}: <b>{{ article.reviewer.name }}</b> ·
                <time :datetime="article.reviewer.date_iso">{{ article.reviewer.date }}</time>
              </span>
            </a>

            <!-- Написать в MAX (мобайл) — та же кнопка, компактная, под звонком -->
            <MaxButton v-if="article.max_link" :href="article.max_link" goal="OPEN_MAX_ARTICLE" compact class="mt-2 w-full" />
            <div v-if="article.lawyer_rating || article.expirience || lawyerRegion" class="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
              <a v-if="article.lawyer_rating" :href="route('lawyer', article.userid)"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white border border-gray-100">
                <RatingReady :rating="article.lawyer_rating" />
                <span class="text-gray-600"><span class="font-semibold text-gray-800">{{ article.lawyer_rating }}</span> · {{ article.lawyer_reviews_count }} {{ reviewWord(article.lawyer_reviews_count) }}</span>
              </a>
              <span v-if="article.expirience"
                class="inline-flex items-center px-2 py-0.5 rounded-lg bg-white text-gray-600 border border-gray-100">
                Стаж {{ article.expirience }} {{ yearWord(article.expirience) }}
              </span>
              <span v-if="lawyerRegion"
                class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-lg bg-white text-gray-600 border border-gray-100">
                📍 {{ lawyerRegion }}
              </span>
            </div>
          </div>

          <!-- call to action (телефон автора перенесён в сайдбар) -->

          <span v-if="vars.article.avito">
            <div v-if="avito"
              class="flex flex-col md:flex-row items-center justify-between my-8 p-6 bg-white rounded-lg shadow-md ">
              <h2 class="text-base text-center font-semibold text-gray-800 mb-2 md:mb-0 px-5">Этот юрист оказывает
                услуги на
                Авито.</h2>
              <a :href="vars.article.avito" target="_blank" @click="trackClick('avito_click')"
                class="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                    d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                    clip-rule="evenodd" />
                </svg>
                Заказать на Авито
              </a>
            </div>
            <div v-else
              class="flex flex-col md:flex-row items-center justify-between my-8 p-6 bg-white rounded-lg shadow-md ">
              <h2 class="text-base text-center font-semibold text-gray-800 mb-2 md:mb-0">Этот юрист оказывает услуги по
                теме статьи</h2>
              <a :href="vars.article.avito" target="_blank" @click="trackClick('usluga_click')"
                class="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                    d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z"
                    clip-rule="evenodd" />
                </svg>
                Заказать услугу
              </a>
            </div>
          </span>

          <!-- Обложка: на мобиле по центру и не выше 420px, на ПК — узкая колонка справа с обтеканием,
               чтобы вертикальная картинка не сжимала текст в полоску -->
          <figure v-if="vars.article.practice_file_path" class="md:hidden text-center mx-auto mb-6">
            <img rel="preload" fetchpriority="high"
              class="mx-auto w-auto max-h-[420px] lg:max-h-none lg:w-full object-contain border border-gray-200 rounded-xl"
              :width="coverDims?.width" :height="coverDims?.height"
              :src="'https://nedicom.ru/' + vars.article.practice_file_path"
              :alt="vars.article.header + ' - практика'" />
            <figcaption v-if="usluga?.usl_name" class="mt-2 text-xs text-gray-500 text-center">
              {{ usluga.usl_name }} — практика
            </figcaption>
          </figure>
          <!-- Автору: платный фрагмент открыт ему, читателям — по коду -->
          <div v-if="paywall.has && paywall.author" class="my-4 flex flex-wrap items-center gap-2 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-900">
            <svg class="h-4 w-4 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11V8a5 5 0 0110 0v3M6 11h12v9H6z" /></svg>
            В статье есть платный фрагмент: вам он виден как автору, читателям — после кода.
            <span class="text-indigo-700">Открыли по коду: <b>{{ paywall.unlocks }}</b>.</span>
            <a :href="route('articles.edit', [article.url])" class="font-medium text-indigo-700 underline">Код — в редакторе</a>
          </div>
          <Teleport v-for="(t, i) in lockedTargets" :key="'pw' + i" :to="t.el">
            <PaywallLock :article-id="vars.article.id" :words="t.words" :headings="t.headings"
              :max-link="article.max_link || ''" :lawyer-name="article.name || ''"
              :article-title="article.header || ''" :article-url="'https://nedicom.ru/articles/' + article.url" @call="openArticlePhone" />
          </Teleport>

          <!-- Мнения экспертов: панель модерации, кнопки «Добавить мнение» у подзаголовков -->
          <ArticleExpertOpinions :opinions="vars.expertOpinions" :article-id="vars.article.id"
            :auth-id="vars.authid" :toc="vars.toc" />

          <!-- Тело статьи; видео (если есть) вставляется в середину текста -->
          <div ref="bodyEl">
            <!-- ⚠ АВТОР И ПРОВЕРКА — ОБТЕКАЕМЫЙ БЛОК, А НЕ КОЛОНКА (ПК). Отдельная левая колонка
                 зажимала текст между собой и оглавлением справа; теперь текст обходит блок, а ниже
                 идёт на всю ширину. Микроразметка автора и дат — здесь; мобильная копия внизу без неё. -->
            <aside class="article-byline hidden md:block">
              <!-- Одна карточка из секций: автор → проверка юристом → связь → сведения о статье.
                   Раньше это были четыре отдельных блока с крупным шрифтом, кнопки разной ширины. -->
              <div class="overflow-hidden rounded-xl border border-gray-200 bg-white text-sm">
                <!-- Автор -->
                <div class="p-3">
                  <div class="flex items-center gap-2.5">
                    <Link :href="route('lawyer', article.userid)" class="relative shrink-0">
                      <img :src="'https://nedicom.ru/' + article.avatar_path" :alt="article.name"
                        class="!w-10 !h-10 rounded-full object-cover" />
                      <span class="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" aria-hidden="true"></span>
                    </Link>
                    <div class="min-w-0">
                      <a :href="route('lawyer', article.userid)" :aria-label="'автор статьи - ' + article.name" class="hover:underline">
                        <span class="block truncate font-semibold leading-tight text-gray-900">{{ article.name }}</span>
                      </a>
                      <span v-if="usluga?.usl_name" class="mt-0.5 block truncate text-xs leading-tight text-indigo-700">{{ usluga.usl_name }}</span>
                    </div>
                  </div>
                  <div v-if="article.lawyer_rating || article.expirience || lawyerRegion" class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
                    <a v-if="article.lawyer_rating" :href="route('lawyer', article.userid)" class="inline-flex items-center gap-1 hover:text-gray-800">
                      <svg class="h-3.5 w-3.5 text-amber-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.07 3.29a1 1 0 00.95.69h3.46c.97 0 1.37 1.24.59 1.81l-2.8 2.03a1 1 0 00-.36 1.12l1.07 3.29c.3.92-.76 1.69-1.54 1.12l-2.8-2.03a1 1 0 00-1.18 0l-2.8 2.03c-.78.57-1.84-.2-1.54-1.12l1.07-3.29a1 1 0 00-.36-1.12L2.98 8.72c-.78-.57-.38-1.81.59-1.81h3.46a1 1 0 00.95-.69l1.07-3.29z"/></svg>
                      <b class="font-semibold text-gray-800">{{ article.lawyer_rating }}</b> · {{ article.lawyer_reviews_count }} {{ reviewWord(article.lawyer_reviews_count) }}
                    </a>
                    <span v-if="article.expirience">стаж {{ article.expirience }} {{ yearWord(article.expirience) }}</span>
                    <span v-if="lawyerRegion">{{ lawyerRegion }}</span>
                  </div>
                </div>

                <!-- Юридическая проверка (ревьюер) — рядом с автором, как рекомендует Google для YMYL-тем.
                     Разметка reviewedBy/lastReviewed — в JSON-LD графе (узел WebPage). -->
                <a v-if="article.reviewer" :href="route('lawyer', article.reviewer.id)"
                  class="flex items-center gap-2.5 border-t border-emerald-100 bg-emerald-50/60 px-3 py-2 hover:bg-emerald-50">
                  <img v-if="article.reviewer.avatar" :src="article.reviewer.avatar" :alt="article.reviewer.name"
                    class="!w-8 !h-8 shrink-0 rounded-full object-cover" loading="lazy" />
                  <span class="min-w-0 flex-1 leading-tight">
                    <span class="flex items-center gap-1 text-xs font-semibold text-emerald-800">
                      <svg class="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                      {{ article.reviewer.female ? "Проверила юрист" : "Проверено юристом" }}
                    </span>
                    <span class="mt-0.5 block text-xs font-medium leading-snug text-gray-900">{{ article.reviewer.name }}</span>
                    <time :datetime="article.reviewer.date_iso" class="block text-xs text-gray-500">{{ article.reviewer.date }}</time>
                  </span>
                </a>

                <!-- Связь: обе кнопки во всю ширину карточки. Красная — одна на экран (звонок). -->
                <div v-if="article.phone || article.max_link" class="space-y-2 border-t border-gray-100 p-3">
                  <button v-if="article.phone" type="button" onclick="window.goal('OPEN_PHONE');" @click="openArticlePhone"
                    class="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 py-2 text-white hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2">
                    <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    <span class="flex flex-col items-start leading-tight">
                      <span class="whitespace-nowrap text-sm font-bold">{{ maskPhone(article.phone) }}</span>
                      <span class="text-xs opacity-90">Показать номер</span>
                    </span>
                  </button>
                  <!-- Написать в MAX — та же кнопка, что в «Контактах» услуги, в компактном виде -->
                  <MaxButton :href="article.max_link" goal="OPEN_MAX_ARTICLE" compact class="article-byline__max w-full" />
                </div>

                <!-- Сведения о статье -->
                <div class="border-t border-gray-100 px-3 py-2.5 text-xs">
                  <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                    <dt class="text-gray-500">Чтение</dt>
                    <dd class="m-0 text-right font-medium text-gray-800">{{ readMinutes }} мин</dd>
                    <dt class="text-gray-500">Опубликовано</dt>
                    <dd class="m-0 text-right font-medium text-gray-800"><time :datetime="publishedIso">{{ humanDate(vars.article.created_at, vars.article.created) }}</time></dd>
                    <dt class="text-gray-500">Обновлено</dt>
                    <dd class="m-0 text-right font-medium text-gray-800"><time :datetime="modifiedIso">{{ humanDate(article.updated_at, article.updated) }}</time></dd>
                  </dl>
                  <div class="mt-2 flex items-center justify-between border-t border-gray-100 pt-2">
                    <ShareButtons :bundle="vars.article" :auth="vars.auth" />
                    <span class="inline-flex items-center gap-1 text-gray-500" :title="'Просмотров: ' + article.counter">
                      <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>
                      {{ Number(article.counter || 0).toLocaleString('ru-RU') }}
                    </span>
                  </div>
                  <!-- Читатель может закрепить нас как источник: Google чаще
                       покажет наши материалы в его ИИ-ответах -->
                  <PreferredSource />
                </div>
              </div>
            </aside>
            <div class="text-left text-lg text-gray-700 leading-relaxed articlebody" v-html="withCover(bodyParts.top)"></div>
            <template v-if="bodyParts.split">
              <!-- VK видео/клип (video_ext) — настоящий плеер, держит соотношение сторон -->
              <div v-if="videoRef && videoRef.type === 'vkvideo'"
                class="my-4 mx-auto lg:float-left lg:mr-6 lg:mb-2"
                :class="isVertical ? 'max-w-[280px] lg:w-[320px]' : 'w-full lg:w-1/2 lg:max-w-none'">
                <div class="relative w-full overflow-hidden rounded-xl bg-black" :style="{ aspectRatio: videoAspect }">
                  <iframe class="absolute inset-0 w-full h-full" :src="videoRef.src" loading="lazy"
                    title="Видео" frameborder="0"
                    allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
                    allowfullscreen></iframe>
                </div>
              </div>
              <!-- VK пост-виджет (легаси, запись целиком) — узкий блок, обтекается текстом -->
              <div v-else-if="videoRef && videoRef.type === 'vk'"
                class="my-4 mx-auto max-w-[280px] sm:max-w-xs lg:float-left lg:w-[340px] lg:max-w-none lg:mr-6 lg:mb-2">
                <VKPost :owner-id="videoRef.owner" :post-id="videoRef.post" :hash="videoRef.hash" />
              </div>
              <!-- легаси-iframe (горизонтальный) — во всю ширину -->
              <div v-else-if="videoRef && videoRef.type === 'iframe'" class="my-6">
                <iframe width="100%" height="500" :src="videoRef.src" loading="lazy"
                  title="Видео" frameborder="0"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture; screen-wake-lock;"
                  allowfullscreen></iframe>
              </div>
              <div class="text-left text-lg text-gray-700 leading-relaxed articlebody" v-html="bodyParts.top ? bodyParts.bottom : withCover(bodyParts.bottom)"></div>
              <div class="clear-both"></div>
            </template>
          </div>

          <!-- Судебная практика по делу (PDF для скачивания) -->
          <div v-if="practicePdfs.length" class="my-8 rounded-xl border border-gray-200 bg-gray-50 p-5">
            <div class="flex items-center gap-2 mb-3">
              <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/></svg>
              <h2 class="text-xl font-bold text-gray-900 m-0">Судебная практика по теме</h2>
            </div>
            <p class="text-sm text-gray-500 mb-3">Реальные судебные решения, на которых основана статья — можно скачать и изучить:</p>
            <ul class="space-y-2 list-none p-0 m-0">
              <li v-for="(p, i) in practicePdfs" :key="i">
                <a :href="'https://nedicom.ru/' + p.path" target="_blank" rel="noopener" download
                  class="inline-flex items-center gap-2 text-base font-medium text-indigo-700 hover:text-indigo-900 hover:underline">
                  <svg class="w-5 h-5 text-red-500 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M7 2a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V8l-6-6H7zm6 1.5L17.5 8H13V3.5zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16z"/></svg>
                  {{ p.name }}
                </a>
              </li>
            </ul>
          </div>

          <!-- Перелинковка на юристов: категория статьи в городе посетителя и хаб города.
               ⚠ Анкоры и адреса не менять — через них вес идёт категории статьи и городу.
               Статический блок «выберите свой город» убран 15.09.2026 по решению владельца. -->
          <nav v-if="usluga || (vars.region && vars.region.url)" aria-label="Юристы по теме статьи"
            class="clear-both my-8 grid gap-3 sm:grid-cols-2">
            <a v-if="vars.region && vars.usluga" :href="'https://nedicom.ru/uslugi/' + vars.region.url + '/' + vars.usluga.url"
              class="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 no-underline transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span class="min-w-0 flex-1">
                <span class="block text-xs font-medium text-gray-500">Найти юриста</span>
                <span class="block text-base font-semibold text-gray-900 leading-snug">{{ usluga.usl_name }} ({{ vars.region.title }})</span>
              </span>
              <svg class="w-5 h-5 shrink-0 text-indigo-500 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </a>
            <a v-else-if="vars.usluga" :href="'https://nedicom.ru/uslugi/all-cities/' + vars.usluga.url"
              class="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 no-underline transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span class="min-w-0 flex-1">
                <span class="block text-xs font-medium text-gray-500">Найти юриста</span>
                <span class="block text-base font-semibold text-gray-900 leading-snug">{{ usluga.usl_name }}</span>
              </span>
              <svg class="w-5 h-5 shrink-0 text-indigo-500 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </a>
            <!-- Город посетителя определён и в нём есть объявления по услуге статьи -->
            <a v-if="userCityLink.mode === 'city'" :href="userCityLink.url"
              class="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 no-underline transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span class="min-w-0 flex-1">
                <span class="block text-xs font-medium text-gray-500">Юристы в вашем городе</span>
                <span class="block text-base font-semibold text-gray-900 leading-snug">{{ userCityLink.title }}</span>
              </span>
              <svg class="w-5 h-5 shrink-0 text-indigo-500 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </a>
            <a v-else-if="userCityLink.mode === 'hub' && vars.region && vars.region.url" :href="'https://nedicom.ru/uslugi/' + vars.region.url"
              class="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 no-underline transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
              <span class="min-w-0 flex-1">
                <span class="block text-xs font-medium text-gray-500">Все юристы города</span>
                <span class="block text-base font-semibold text-gray-900 leading-snug">Юрист в городе {{ vars.region.title }}</span>
              </span>
              <svg class="w-5 h-5 shrink-0 text-indigo-500 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </a>
          </nav>

          <!-- Комментарии — той же ширины, что текст статьи (внутри её колонки) -->
          <div class="w-full">
            <Answer :answerclass="'w-full py-8 bg-white overflow-hidden'" :question="vars.article"
              :authid="vars.authid" :type="'article'" :article_id="vars.article.id" :subcomments="true" />
          </div>

          <div id="comment" class="scroll-mt-20">
            <Answers class="w-full py-8" :answers="vars.answers"
              :question="vars.question ? vars.question : null" :authid="vars.authid" :type="'article'"
              :article_id="vars.article.id" />
          </div>
          </div>

          <!-- Правая колонка (≥1024px): липкое оглавление. На 1280 было поздно: ноутбук 1366 с масштабом 125% — это ~1090 px, и колонка не появлялась. Только в браузере. Звонок сюда не ставим:
               красная кнопка одна на экран, и она уже в блоке автора. Колонка тянется до конца комментариев. -->
          <aside v-if="mounted && hasToc" class="hidden lg:block w-60 xl:w-72 shrink-0">
            <div class="sticky top-20 pt-2">
              <ArticleToc :items="vars.toc" variant="rail" />
            </div>
          </aside>
          </div>

          <!-- Похожие статьи (та же категория) — под комментариями: перелинковка, контент-кластер -->
          <RelatedArticles :articles="vars.relatedArticles" :practice="vars.relatedPractice" />
        </div>



      </div>


      <!--
      <Chat
        :user="vars.article"
        :usluga="vars.usluga"
        class="hidden md:block"
      />
    -->
    </div>



    <Transition enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4" enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 translate-y-4">
    <div v-show="showBottomNav"
      class="fixed left-1/2 bottom-0 -translate-x-1/2 flex items-center p-2 mb-4 text-gray-500 bg-white/95 backdrop-blur rounded-xl z-50 shadow-lg ring-1 ring-gray-900/5"
      role="menu">
      <button v-if="hasToc" type="button" @click="openToc" aria-label="Содержание"
        class="lg:hidden inline-flex items-center gap-2 h-8 px-3 mx-1 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
        </svg>
        Содержание
      </button>
      <!-- «Задать вопрос» (человечек) убран 15.09.2026: без подписи его не понимали. «Наверх» вернули 16.09.2026 -->
      <button type="button" aria-label="Наверх" title="Наверх"
        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 mx-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        @click="scrollToTop">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19V5m0 0l-6 6m6-6l6 6" /></svg>
      </button>
      <a href="#comment" aria-label="Комментарии"
        class="inline-flex items-center justify-center gap-1 shrink-0 h-8 px-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg mx-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2">
        <svg class="w-6 h-6 " aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M7 9h5m3 0h2M7 12h2m3 0h5M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6.616a1 1 0 0 0-.67.257l-2.88 2.592A.5.5 0 0 1 8 18.477V17a1 1 0 0 0-1-1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
        </svg>
        <span>{{ vars.totalComments }}</span>
      </a>

    </div>
    </Transition>

    <!-- Блок «Последние вопросы» убран 15.09.2026: 20 свежих вопросов категории не по теме статьи
         (павильоны, микрозаймы в статье про рекламу) размывали тематику страницы для поиска
         и тянули в ответ полные тексты вопросов. Перелинковку дают «Статьи по теме». -->

  </Body>

  <!-- <Sidebaraction :ModalBtnText="ModalBtnText" /> -->
  <MainFooter />

  <Tracking :key="backendurl" :tracking="$page.props.tracking" :backendurl="vars.backendurl" />
</template>


<style>
/* Аватар автора в сайдбаре */
a img {
  border-radius: 50%;
  width: 80px;
}

/* ── Типографика тела статьи ── */
/* ── Оформление из редактора статей ── */
.articlebody mark { background: #fde68a; color: inherit; border-radius: .15em; padding: 0 .1em; box-decoration-break: clone; -webkit-box-decoration-break: clone; }
.articlebody mark[data-color="green"] { background: #bbf7d0; }
.articlebody mark[data-color="pink"] { background: #fbcfe8; }

/* Подсказка к термину: пунктир, текст всплывает над словом при наведении, фокусе или нажатии */
.articlebody span[data-tip] { position: relative; border-bottom: 2px dotted #6366f1; cursor: help; outline: none; }
.articlebody span[data-tip]::after {
  content: attr(data-tip);
  position: absolute; left: 0; bottom: calc(100% + .5rem); z-index: 30;
  width: max-content; max-width: min(18rem, 80vw);
  padding: .5rem .75rem; border-radius: .5rem;
  background: #111827; color: #fff; font-size: .875rem; font-weight: 400; line-height: 1.45; text-align: left;
  box-shadow: 0 10px 25px -10px rgb(0 0 0 / .4);
  opacity: 0; visibility: hidden; transform: translateY(4px); transition: opacity .15s, transform .15s, visibility .15s;
  pointer-events: none; white-space: normal;
}
.articlebody span[data-tip][data-tip-left]::after { left: auto; right: 0; }
.articlebody span[data-tip]:hover::after,
.articlebody span[data-tip]:focus::after { opacity: 1; visibility: visible; transform: none; }
.articlebody span[data-tip]:focus-visible { box-shadow: 0 0 0 2px #c7d2fe; border-radius: .15em; }
@media (prefers-reduced-motion: reduce) { .articlebody span[data-tip]::after { transition: none; } }

/* Выноски: Важно / Совет / Внимание */
.articlebody div[data-callout] {
  display: flow-root; position: relative; margin: 1.5rem 0; padding: 2.25rem 1.25rem 1rem;
  border-radius: .75rem; background: #eef2ff; border: 1px solid #e0e7ff; color: #1f2937;
}
.articlebody div[data-callout]::before {
  content: "Важно"; position: absolute; top: .75rem; left: 1.25rem;
  font-size: .75rem; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #4338ca;
}
.articlebody div[data-callout] > :last-child { margin-bottom: 0; }
.articlebody div[data-callout="tip"] { background: #ecfdf5; border-color: #d1fae5; }
.articlebody div[data-callout="tip"]::before { content: "Совет"; color: #047857; }
.articlebody div[data-callout="warning"] { background: #fffbeb; border-color: #fde68a; }
.articlebody div[data-callout="warning"]::before { content: "Внимание"; color: #b45309; }

/* Платный фрагмент */
.articlebody .paywall { display: flow-root; margin: 1.5rem 0; }
.articlebody .paywall--open { position: relative; padding-left: 1rem; border-left: 3px solid #c7d2fe; }
.articlebody .paywall > :first-child, .articlebody div[data-callout] > :first-child { margin-top: 0; }
.articlebody .paywall__note { margin: 0; padding: 1rem 1.25rem; border: 1px dashed #c7d2fe; border-radius: .75rem; color: #4338ca; font-size: 1rem; }
.articlebody .paywall--locked:has(.paywall-card) .paywall__note { display: none; }

/* Блок автора и проверки на ПК — плавает слева, текст его обтекает */
/* MAX в карточке автора: без увеличения при наведении и тени — кнопка во всю ширину узкой колонки */
.article-byline .article-byline__max { box-shadow: none; }
.article-byline .article-byline__max:hover { transform: none; background: #f9fafb; }
.article-byline { float: left; width: 15rem; margin: .25rem 2rem 1rem 0; }
@media (min-width: 1024px) { .article-byline { width: 16rem; } }
.articlebody .article-cover { display: none; }
@media (min-width: 768px) {

  .articlebody .article-cover { display: block; float: right; width: 15rem; margin: .25rem 0 1rem 2rem; text-align: center; }
  .articlebody .article-cover img { margin: 0; width: 100%; border: 1px solid #e5e7eb; box-shadow: none; }
  .articlebody .article-cover figcaption { margin-top: .5rem; font-size: .75rem; color: #6b7280; line-height: 1.4; }
}
/* Рядом с плавающим блоком фон, рамки и маркеры не уходят под него — блоки сжимаются по ширине */
.articlebody ul, .articlebody ol, .articlebody blockquote,
.articlebody .expert-opinion, .articlebody h2 + p { display: flow-root; }

.articlebody {
  line-height: 1.7;          /* 1.8 разваливал абзацы на отдельные строки, особенно на мобиле */
  color: #374151;            /* gray-700 — мягче, чем 800 */
  max-width: 100%;           /* ширину держит колонка статьи (52rem): рядом с блоком автора строка короче, ниже — ~80 симв. */
}
@media (max-width: 640px) {
  .articlebody { font-size: 1.0625rem; }
}

.articlebody p {
  margin: 0 0 1.25rem;
  padding: 0;
}

/* Лид-абзац — журнальный «вход» в текст */
.articlebody > p:first-child {
  font-size: 1.2rem;
  line-height: 1.75;
  color: #1f2937;
}

/* Капсула-ответ: первый абзац после H2 — слегка акцентируем для сканирования */
.articlebody h2 + p {
  font-weight: 500;
  color: #1f2937;
  border-left: 3px solid #e0e7ff;
  padding-left: 1rem;
}

.articlebody h2 {
  font-size: 1.6rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
  margin: 3rem 0 1rem;
  text-wrap: balance;        /* без «висячего» слова на 2-й строке */
  scroll-margin-top: 90px;   /* переход из оглавления — заголовок не под шапкой */
}

.articlebody h3 {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.35;         /* иначе наследует 1.7 тела и двухстрочный заголовок расползается */
  color: #1f2937;
  margin: 2rem 0 .75rem;
  text-wrap: balance;
  scroll-margin-top: 90px;
}

.articlebody h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 1.5rem 0 .5rem;
}

.articlebody ul,
.articlebody ol {
  margin: 1rem 0 1.25rem;
  padding-left: 1.5rem;
}

.articlebody ul { list-style: disc; }
.articlebody ol { list-style: decimal; }
.articlebody li { margin: .5rem 0; padding-left: .15rem; }
/* Редактор кладёт текст пункта в <p> — без этого между пунктами висели абзацные отступы */
.articlebody li > p { margin: 0; }
.articlebody li > p + p { margin-top: .25rem; }
.articlebody p:empty { display: none; }

/* Нумерованный пункт из нескольких абзацев (разбор дела: заголовок, что было, итог) — карточка */
.articlebody ol:has(> li > p + p) {
  list-style: none;
  padding-left: 0;
  counter-reset: card;
  display: grid;
  gap: .75rem;
}
.articlebody ol > li:has(> p + p) {
  counter-increment: card;
  position: relative;
  margin: 0;
  padding: 1rem 1rem 1rem 3rem;
  border: 1px solid #e5e7eb;
  border-radius: .75rem;
  background: #fff;
  font-size: 1rem;
  line-height: 1.6;
}
.articlebody ol > li:has(> p + p)::before {
  content: counter(card);
  position: absolute;
  left: 1rem;
  top: 1rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 700;
  font-size: .85rem;
  display: grid;
  place-items: center;
}
.articlebody ol > li:has(> p + p) > p:first-child { color: #111827; }
@media (max-width: 640px) {
  .articlebody ol > li:has(> p + p) { padding: .75rem .75rem .75rem 2.5rem; }
  .articlebody ol > li:has(> p + p)::before { left: .5rem; top: .75rem; width: 1.5rem; height: 1.5rem; font-size: .75rem; }
}
.articlebody li::marker { color: #6366f1; }   /* indigo-маркеры */

.articlebody a {
  color: #4f46e5;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.articlebody a:hover { color: #4338ca; }

/* картинки в тексте — не круглые 80px (перебиваем глобальное a img) */
.articlebody img,
.articlebody a img {
  width: auto;
  max-width: 100%;
  height: auto;
  border-radius: .75rem;
  margin: 1.5rem auto;
  box-shadow: 0 6px 20px rgba(17, 24, 39, .08);
}

/* цитата — мягкая indigo-врезка вместо резкой красной полосы */
.articlebody blockquote {
  margin: 1.75rem 0;
  padding: 1rem 1.25rem;
  border-left: 4px solid #6366f1;
  background: #eef2ff;
  border-radius: .5rem;
  color: #312e81;
  font-style: normal;
  min-height: 0;
}

.articlebody table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: .95rem;
}
.articlebody th,
.articlebody td {
  border: 1px solid #e5e7eb;
  padding: .55rem .8rem;
  text-align: left;
}
.articlebody th { background: #f9fafb; font-weight: 700; }
.articlebody strong { color: #111827; }

/* Широкие таблицы на мобиле — горизонтальный скролл вместо ломки верстки */
@media (max-width: 640px) {
  .articlebody { max-width: 100%; }
  .articlebody table {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
  }
}

/* Мнение эксперта из отдельной таблицы (сервер вставляет под подзаголовком) */
.articlebody .expert-opinion {
  margin: .5rem 0 1.5rem;
  padding: 1rem 1.15rem;
  border-left: 4px solid #6366f1;
  background: #eef2ff;
  border-radius: .75rem;
}
.articlebody .expert-opinion::before {
  content: "Мнение эксперта";
  display: block;
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: #4f46e5;
  margin-bottom: .5rem;
}
.articlebody .expert-opinion__who {
  display: flex;
  align-items: center;
  gap: .6rem;
  text-decoration: none;
  color: #111827;
  margin-bottom: .5rem;
}
.articlebody .expert-opinion__who img {
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  object-fit: cover;
  margin: 0;
  box-shadow: none;
}
.articlebody .expert-opinion__who span { display: flex; flex-direction: column; line-height: 1.25; }
.articlebody .expert-opinion__who b { font-size: .95rem; }
.articlebody .expert-opinion__who small { font-size: .8rem; color: #6b7280; font-weight: 400; }
.articlebody .expert-opinion__text { font-size: 1rem; line-height: 1.65; color: #1f2937; }
/* «Изменить / Удалить» — только автору мнения, автору статьи и админу (вставляется в браузере) */
.articlebody .expert-opinion__actions { display: flex; gap: .5rem; margin-top: .6rem; }
.articlebody .expert-opinion__actions button {
  padding: .15rem .6rem; font-size: .8rem; color: #4b5563; background: #fff;
  border: 1px solid #d1d5db; border-radius: .5rem; cursor: pointer;
}
.articlebody .expert-opinion__actions button:hover { background: #f9fafb; }
.articlebody .expert-opinion__actions button:last-child { color: #be123c; }

/* Кнопка «Добавить мнение» под подзаголовком — видна только юристам (вставляется в браузере) */
.articlebody .expert-opinion-add {
  display: inline-block;
  margin: -.5rem 0 .9rem;
  padding: .2rem .6rem;
  font-size: .8rem;
  color: #6366f1;
  background: transparent;
  border: 1px dashed #c7d2fe;
  border-radius: .5rem;
  cursor: pointer;
}
.articlebody .expert-opinion-add:hover { background: #eef2ff; }

/* «Мнение эксперта» — врезка. Класс artlwrhref сервер оставляет ТОЛЬКО ссылке на
   профиль юриста с фото (normalizeBodyLinks); на всякий случай подпись требует и фото
   внутри — чтобы обычная ссылка не превратилась в «мнение эксперта». */
.artlwrhref {
  vertical-align: top;
  max-width: 100%;
  border: none;
}
.artlwrhref:has(img)::before {
  content: "Мнение эксперта";
  font-weight: bold;
  display: block;
  margin-bottom: 5px;
  font-size: 1.2rem;
}
</style>
